/**
 * AchievementSystem - Tracks and awards achievements
 */

import { eventBus, GameEvents } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';
import { STORAGE_KEYS } from '../core/Constants.js';

// Achievement definitions
const ACHIEVEMENTS = {
  // Catching achievements
  first_catch: {
    id: 'first_catch',
    name: 'First Catch',
    description: 'Catch your first fish',
    icon: '🐟',
    category: 'catching',
    check: (state) => state.fishCaught >= 1,
    reward: { points: 50 }
  },
  catch_10: {
    id: 'catch_10',
    name: 'Getting Started',
    description: 'Catch 10 fish',
    icon: '🥉',
    category: 'catching',
    check: (state) => state.fishCaught >= 10,
    reward: { points: 100 }
  },
  catch_25: {
    id: 'catch_25',
    name: 'Dedicated Angler',
    description: 'Catch 25 fish',
    icon: '🥈',
    category: 'catching',
    check: (state) => state.fishCaught >= 25,
    reward: { points: 200 }
  },
  catch_50: {
    id: 'catch_50',
    name: 'Experienced Angler',
    description: 'Catch 50 fish',
    icon: '🥇',
    category: 'catching',
    check: (state) => state.fishCaught >= 50,
    reward: { points: 500 }
  },
  catch_100: {
    id: 'catch_100',
    name: 'Master Fisherman',
    description: 'Catch 100 fish',
    icon: '🏆',
    category: 'catching',
    check: (state) => state.fishCaught >= 100,
    reward: { points: 1000 }
  },

  // Rarity achievements
  first_uncommon: {
    id: 'first_uncommon',
    name: 'Lucky Find',
    description: 'Catch an uncommon fish',
    icon: '💚',
    category: 'rarity',
    check: (state, data) => data?.fish?.rarity === 'uncommon',
    triggerOn: 'fish_caught',
    reward: { points: 75 }
  },
  first_rare: {
    id: 'first_rare',
    name: 'Rare Discovery',
    description: 'Catch a rare fish',
    icon: '💛',
    category: 'rarity',
    check: (state, data) => data?.fish?.rarity === 'rare',
    triggerOn: 'fish_caught',
    reward: { points: 150 }
  },
  first_legendary: {
    id: 'first_legendary',
    name: 'Legendary!',
    description: 'Catch a legendary fish',
    icon: '💜',
    category: 'rarity',
    check: (state, data) => data?.fish?.rarity === 'legendary',
    triggerOn: 'fish_caught',
    reward: { points: 500 }
  },

  // Collection achievements
  discover_5: {
    id: 'discover_5',
    name: 'Curious Explorer',
    description: 'Discover 5 different species',
    icon: '📖',
    category: 'collection',
    check: (state) => getDiscoveredCount(state) >= 5,
    reward: { points: 100 }
  },
  half_collection: {
    id: 'half_collection',
    name: 'Halfway There',
    description: 'Discover 9 different species',
    icon: '📚',
    category: 'collection',
    check: (state) => getDiscoveredCount(state) >= 9,
    reward: { points: 250 }
  },
  full_collection: {
    id: 'full_collection',
    name: 'Completionist',
    description: 'Discover all 18 species',
    icon: '🏅',
    category: 'collection',
    check: (state) => getDiscoveredCount(state) >= 18,
    reward: { points: 2000 }
  },

  // All common fish
  all_common: {
    id: 'all_common',
    name: 'Common Collector',
    description: 'Catch all common fish species',
    icon: '💙',
    category: 'collection',
    check: (state) => hasAllOfRarity(state, 'common'),
    reward: { points: 200 }
  },

  // All uncommon fish
  all_uncommon: {
    id: 'all_uncommon',
    name: 'Uncommon Collector',
    description: 'Catch all uncommon fish species',
    icon: '💚',
    category: 'collection',
    check: (state) => hasAllOfRarity(state, 'uncommon'),
    reward: { points: 400 }
  },

  // All rare fish
  all_rare: {
    id: 'all_rare',
    name: 'Rare Collector',
    description: 'Catch all rare fish species',
    icon: '💛',
    category: 'collection',
    check: (state) => hasAllOfRarity(state, 'rare'),
    reward: { points: 800 }
  },

  // Skill achievements
  streak_3: {
    id: 'streak_3',
    name: 'Nice Streak',
    description: 'Catch 3 fish in a row without missing',
    icon: '🔥',
    category: 'skill',
    check: (state) => state.consecutiveCatches >= 3,
    reward: { points: 50 }
  },
  streak_5: {
    id: 'streak_5',
    name: 'On a Roll',
    description: 'Catch 5 fish in a row without missing',
    icon: '🔥🔥',
    category: 'skill',
    check: (state) => state.consecutiveCatches >= 5,
    reward: { points: 150 }
  },
  streak_10: {
    id: 'streak_10',
    name: 'Unstoppable',
    description: 'Catch 10 fish in a row without missing',
    icon: '🔥🔥🔥',
    category: 'skill',
    check: (state) => state.consecutiveCatches >= 10,
    reward: { points: 500 }
  },

  // Specialist achievements
  specialist: {
    id: 'specialist',
    name: 'Species Specialist',
    description: 'Catch 10 of the same species',
    icon: '🔬',
    category: 'collection',
    check: (state) => hasSpecialistFish(state, 10),
    reward: { points: 150 }
  },

  // Exploration achievements
  explorer_3: {
    id: 'explorer_3',
    name: 'Pond Hopper',
    description: 'Fish in 3 different ponds',
    icon: '🗺️',
    category: 'exploration',
    check: (state) => state.pondsFished?.size >= 3,
    reward: { points: 100 }
  },
  explorer_all: {
    id: 'explorer_all',
    name: 'World Traveler',
    description: 'Fish in all 8 ponds',
    icon: '🌍',
    category: 'exploration',
    check: (state) => state.pondsFished?.size >= 8,
    reward: { points: 300 }
  }
};

// Helper functions
function getDiscoveredCount(state) {
  if (!state.inventory) return 0;
  return Object.values(state.inventory).filter(entry => entry.discovered).length;
}

function hasAllOfRarity(state, rarity) {
  if (!state.inventory) return false;
  const fishOfRarity = Object.values(state.inventory).filter(
    entry => entry.fish?.rarity === rarity
  );
  return fishOfRarity.length > 0 && fishOfRarity.every(entry => entry.discovered);
}

function hasSpecialistFish(state, count) {
  if (!state.inventory) return false;
  return Object.values(state.inventory).some(entry => entry.count >= count);
}

class AchievementSystem {
  constructor() {
    this.unlocked = {};
    this.loadUnlocked();
    this.setupEventListeners();
  }

  /**
   * Load unlocked achievements from localStorage
   */
  loadUnlocked() {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.ACHIEVEMENTS);
      if (saved) {
        this.unlocked = JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Failed to load achievements:', error);
      this.unlocked = {};
    }
  }

  /**
   * Save unlocked achievements to localStorage
   */
  saveUnlocked() {
    try {
      localStorage.setItem(STORAGE_KEYS.ACHIEVEMENTS, JSON.stringify(this.unlocked));
    } catch (error) {
      console.warn('Failed to save achievements:', error);
    }
  }

  /**
   * Set up event listeners for achievement triggers
   */
  setupEventListeners() {
    // Check achievements when fish is caught
    eventBus.on(GameEvents.FISH_CAUGHT, (data) => {
      this.checkAchievements(data);
    });

    // Check achievements when minigame fails (reset streak)
    eventBus.on(GameEvents.MINIGAME_FAILURE, () => {
      // Streak is reset in GameState
    });
  }

  /**
   * Check all achievements against current state
   * @param {Object} eventData - Optional event-specific data
   */
  checkAchievements(eventData = null) {
    const state = gameState.state;

    Object.entries(ACHIEVEMENTS).forEach(([id, achievement]) => {
      // Skip if already unlocked
      if (this.unlocked[id]) return;

      // Check if achievement condition is met
      let conditionMet = false;

      try {
        if (achievement.triggerOn === 'fish_caught' && eventData) {
          // This achievement only triggers on specific events
          conditionMet = achievement.check(state, eventData);
        } else if (!achievement.triggerOn) {
          // General achievement, check state
          conditionMet = achievement.check(state, eventData);
        }
      } catch (error) {
        console.warn(`Error checking achievement ${id}:`, error);
      }

      if (conditionMet) {
        this.unlock(id);
      }
    });
  }

  /**
   * Unlock an achievement
   * @param {string} id - Achievement ID
   */
  unlock(id) {
    if (this.unlocked[id]) return;

    const achievement = ACHIEVEMENTS[id];
    if (!achievement) return;

    // Mark as unlocked
    this.unlocked[id] = {
      unlockedAt: new Date().toISOString()
    };

    // Save to localStorage
    this.saveUnlocked();

    // Apply reward
    if (achievement.reward?.points) {
      const currentScore = gameState.get('score');
      gameState.set('score', currentScore + achievement.reward.points);
    }

    // Emit achievement unlocked event
    eventBus.emit(GameEvents.ACHIEVEMENT_UNLOCKED, {
      id,
      achievement,
      reward: achievement.reward
    });

    console.log(`Achievement unlocked: ${achievement.name}`);
  }

  /**
   * Get achievement by ID
   * @param {string} id - Achievement ID
   */
  getAchievement(id) {
    return ACHIEVEMENTS[id];
  }

  /**
   * Get all achievements
   */
  getAllAchievements() {
    return ACHIEVEMENTS;
  }

  /**
   * Get achievements as array (for UI iteration)
   */
  get achievements() {
    return Object.values(ACHIEVEMENTS);
  }

  /**
   * Get unlocked achievements as array
   */
  getUnlockedAchievements() {
    return Object.values(ACHIEVEMENTS).filter(a => this.isUnlocked(a.id));
  }

  /**
   * Get achievement progress
   */
  getProgress() {
    const total = Object.keys(ACHIEVEMENTS).length;
    const unlocked = Object.keys(this.unlocked).length;
    return {
      total,
      unlocked,
      percentage: Math.round((unlocked / total) * 100)
    };
  }

  /**
   * Get achievements by category
   * @param {string} category - Category name
   */
  getByCategory(category) {
    return Object.values(ACHIEVEMENTS).filter(a => a.category === category);
  }

  /**
   * Check if achievement is unlocked
   * @param {string} id - Achievement ID
   */
  isUnlocked(id) {
    return !!this.unlocked[id];
  }

  /**
   * Reset all achievements (for testing or new game)
   */
  reset() {
    this.unlocked = {};
    this.saveUnlocked();
  }
}

// Singleton instance
export const achievementSystem = new AchievementSystem();

export { ACHIEVEMENTS };
export default AchievementSystem;
